import type {Thread, Comment} from '@github-ui/diff-lines/conversation-types'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useMarkersActions, usePendingReviewActions} from '../stores/selectors'
import {threadPreviewsQueryKey} from '../page-data/payloads/thread-previews'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'

type MutationData = {
  text: string
  inReplyTo: number | null | undefined
  submitBatch?: boolean
  path: string
  comparisonStartOid?: string | null | undefined
  comparisonEndOid?: string | null | undefined
}

export type MutationResponse = {
  thread: Thread
  comment: Comment
  totalCommentsCount: number
}

export function useCreateReplyCommentMutation(filePath: string, lineMarkersKey: LineKey | FileKey) {
  const {
    pullRequest: {pathName: basePath},
  } = useRoutePayload<FilesRoutePayload>()
  const queryClient = useQueryClient()
  const apiUrl = `${basePath}/page_data/${PageData.createReviewComment}`
  const {updateThread} = useMarkersActions()

  const {addPendingComment, clearPendingReview} = usePendingReviewActions()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const result = await reactFetchJSON(`${apiUrl}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: data,
      })

      const json = await parseJSONWithBetterErrors(result)
      throwErrorsIfBadResponse(result, json)
      return json
    },
    onSuccess: (data: MutationResponse, inputs: MutationData) => {
      if (inputs.submitBatch !== undefined && !inputs.submitBatch && data.thread) {
        addPendingComment(Number(data.thread.id))
      }

      // Clear the pending review IDs if the comment is submitted as part of a batch
      if (inputs.submitBatch) {
        clearPendingReview()
      }

      // update the Markers Store
      updateThread(Number(data.thread.id), filePath, lineMarkersKey, _ => data.thread)

      // invalidate pullRequestFilesToolbar queries
      return queryClient.invalidateQueries({
        queryKey: threadPreviewsQueryKey(basePath),
      })
    },
  })
}
